import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { UserPlus, Save, CheckCircle2, RotateCcw } from 'lucide-react';
import { Card } from '@components/base/layout';
import { actionAnchorId } from '../configs/otherActionsConfig';
import { Text, Icon } from '@components/base/display';
import { Button } from '@components/base/actions';
import { EntityForm, useEntityForm } from '@components/integrated/entity-form';
import type {
  EntityFormConfig,
  SelectFieldOptions,
  CheckboxFieldOptions,
  UseEntityFormReturn,
} from '@components/integrated/entity-form';
import {
  applyLayoutOverrides,
  buildPublishedConfigFromEntityForm,
  resolveCardTitle,
  usePublishLayoutConfig,
} from '@components/shared/configurable';
import type { LayoutOverrides } from '@components/shared/configurable';
import { claimflowApiClient } from '../api/claimflowApiClient';
import { useClaimDataStore } from './claimDataStore';

interface IntervenantFormProps {
  ses: string;
  idSin: string;
  /**
   * Tenant-tunable allow-list of `CD_CATEG` codes that constrains the
   * Catégorie select options. The categories are fetched internally (not a
   * prop), so a rule author limits the visible options by `setProp`-ing this
   * allow-list (HOC-injected) — mirroring `SettlementCard.allowedNatures`.
   * `undefined` or an empty array ⇒ NO constraint (every fetched category is
   * shown — byte-identical baseline render). Only the option list is
   * filtered; the field's selection state and the submit path are unaffected
   * (the `categ` value still flows through verbatim).
   */
  allowedCategories?: string[];
  /**
   * Tenant-tunable initial `categ` value (a `CD_CATEG` code) seeding the
   * Catégorie select. The seeded value flows through the existing
   * `form.getValue('categ')` mirror, so the dependent "Identité" persons
   * fetch fires exactly as it would for a manual selection. `undefined` ⇒
   * the `categ` key is absent from `initialValues` (byte-identical baseline
   * render). Tenant-overridable via `setProp`.
   */
  defaultCategory?: string;
  /**
   * Tenant-tunable initial `existant` value — `true` (the literal fallback)
   * opens the form on the "existing intervenant" path, `false` on the "new
   * intervenant" creation path. Combined with a `hideField existant` layout
   * effect this **freezes** the creation mode: the Existant checkbox
   * disappears and the form is locked on whichever path this prop selects.
   * Tenant-overridable via `setProp`.
   */
  defaultExistant?: boolean;
  /**
   * Tenant-tunable map of `CD_CATEG` code → the `existant` value to apply
   * when the handler selects that category (e.g. `{ EXPERT: true,
   * TIERS: false }`). Applied on the **transition** of the `categ` form
   * value only, so the handler can still toggle the Existant checkbox
   * afterwards; pair it with a `setFieldCondition existant … { disabled:
   * true }` layout effect to lock the box on the mapped state. A category
   * absent from the map (or a cleared selection) leaves `existant`
   * untouched — there is no revert to a default. When `defaultCategory` is
   * itself mapped, the mapped value seeds `initialValues.existant` and wins
   * over `defaultExistant` (more specific), so the first paint is already
   * correct and the form is not dirty on mount. `undefined` or an empty map
   * ⇒ NO effect at all (byte-identical baseline render). Tenant-overridable
   * via `setProp`.
   */
  existantByCategory?: Record<string, boolean>;
  /**
   * Per-tenant field layout overrides injected by the `withComponentRules`
   * HOC. Underscored to match the injected meta-prop convention; `undefined`
   * at baseline ⇒ the form config is returned unchanged (byte-identical
   * render).
   */
  _layoutOverrides?: LayoutOverrides;
}

interface CategoryOption {
  CD_CATEG: string;
  LIB_CATEG: string;
}

interface PersonOption {
  ID_PER: number;
  LIB: string;
}

interface CompanyOption {
  CD_CIE: string;
  NOM: string;
}

interface ErpProxyResponse<T> {
  success: boolean;
  data: { total: number; data: T[] };
  error?: { code: string; message: string } | null;
}

interface IntervenantFormInnerProps extends IntervenantFormProps {
  /**
   * Called when the user wants to chain a NEW intervenant after a successful
   * submit. The outer component bumps a key counter to remount this inner
   * component, yielding an exact fresh-mount state (empty form, collapsed
   * editing card, active submit button, cleared submission status).
   */
  onResetForm: () => void;
}

function IntervenantFormInner({
  ses,
  idSin,
  allowedCategories,
  defaultCategory,
  defaultExistant,
  existantByCategory,
  onResetForm,
  _layoutOverrides,
}: Readonly<IntervenantFormInnerProps>) {
  const [categories, setCategories] = useState<CategoryOption[]>([]);
  const [persons, setPersons] = useState<PersonOption[]>([]);
  const [companies, setCompanies] = useState<CompanyOption[]>([]);
  const [loadingPersons, setLoadingPersons] = useState(false);
  const [selectedCateg, setSelectedCateg] = useState<string>('');
  const [existant, setExistant] = useState(true);
  const [submittedLabel, setSubmittedLabel] = useState<string | null>(null);

  // Collapsed on first paint — every card in the claim view's right column
  // opens folded. Driven by state + `onExpandChange` rather than a literal
  // `defaultExpanded={false}`, because `Card` re-applies a literal default on
  // every parent re-render, which would fold the card again mid-edit.
  const [expanded, setExpanded] = useState(false);

  const formRef = useRef<UseEntityFormReturn | null>(null);

  // Fetch categories on mount
  useEffect(() => {
    let cancelled = false;
    async function fetchCategories() {
      try {
        const response =
          await claimflowApiClient.get<ErpProxyResponse<CategoryOption>>(
            '/claimflow/erp/registries/veos/endpoints/boby-ws2-gs-sel-categ-sinper',
            { params: { SES: ses, perPage: 9999 } },
          );
        if (!cancelled && response.data.success) {
          setCategories(response.data.data.data);
        }
      } catch {
        // silently fail — categories will remain empty
      }
    }
    fetchCategories();
    return () => {
      cancelled = true;
    };
  }, [ses]);

  // Fetch companies on mount
  useEffect(() => {
    let cancelled = false;
    async function fetchCompanies() {
      try {
        const response =
          await claimflowApiClient.get<ErpProxyResponse<CompanyOption>>(
            '/claimflow/erp/registries/veos/endpoints/boby-ws2-gs-sel-compagnie',
            { params: { perPage: 9999 } },
          );
        if (!cancelled && response.data.success) {
          setCompanies(response.data.data.data);
        }
      } catch {
        // silently fail
      }
    }
    fetchCompanies();
    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch persons when category changes and existant is checked
  useEffect(() => {
    if (!existant || !selectedCateg) {
      setPersons([]);
      return;
    }

    let cancelled = false;
    async function fetchPersons() {
      setLoadingPersons(true);
      try {
        const response =
          await claimflowApiClient.get<ErpProxyResponse<PersonOption>>(
            '/claimflow/erp/registries/veos/endpoints/boby-ws2-gs-sel-personne',
            { params: { SES: ses, cd_categ: selectedCateg, perPage: 9999 } },
          );
        if (!cancelled && response.data.success) {
          setPersons(response.data.data.data);
        }
      } catch {
        // silently fail
      } finally {
        if (!cancelled) setLoadingPersons(false);
      }
    }
    fetchPersons();
    return () => {
      cancelled = true;
    };
  }, [ses, selectedCateg, existant]);

  const categoryItems = useMemo(() => {
    // When the tenant supplies a non-empty `allowedCategories` allow-list,
    // keep only categories whose `CD_CATEG` is in it before mapping to select
    // items. An `undefined`/empty list short-circuits to the full set
    // (baseline parity).
    const allowSet =
      allowedCategories && allowedCategories.length > 0
        ? new Set(allowedCategories)
        : null;
    const source = allowSet
      ? categories.filter((c) => allowSet.has(c.CD_CATEG))
      : categories;
    return source.map((c) => ({ value: c.CD_CATEG, label: c.LIB_CATEG }));
  }, [categories, allowedCategories]);

  const personItems = useMemo(
    () => persons.map((p) => ({ value: String(p.ID_PER), label: p.LIB })),
    [persons],
  );

  const companyItems = useMemo(
    () => companies.map((c) => ({ value: c.CD_CIE, label: c.NOM })),
    [companies],
  );

  const newStakeholderCondition = {
    when: 'existant',
    operator: 'equals' as const,
    value: false,
    effect: { visible: true },
  };

  const tiersCondition = {
    when: 'categ',
    operator: 'equals' as const,
    value: 'TIERS',
    effect: { visible: true },
  };

  const mutationFn = useCallback(
    async (payload: Record<string, unknown> | FormData) => {
      const data =
        payload instanceof FormData
          ? Object.fromEntries(payload.entries())
          : payload;

      if (data.existant) {
        // Existing person → sinper endpoint
        const body: Record<string, unknown> = {
          idSin,
          categ: String(data.categ ?? ''),
          idPer: String(data.idPer ?? ''),
        };

        const response = await claimflowApiClient.post(
          '/claimflow/erp/registries/veos/endpoints/sinper',
          { data: body },
        );
        return response.data;
      }

      // New person → workflow
      const contextData: Record<string, string> = {
        idSin,
        categ: String(data.categ ?? ''),
      };

      const fieldMap: Record<string, string> = {
        nom: 'nom',
        prenom: 'prenom',
        type: 'type',
        mail: 'mail',
        tel: 'tel',
        adresse1: 'adresse1',
        adresse2: 'adresse2',
        cp: 'cp',
        ville: 'ville',
        ref: 'ref',
        cdCie: 'cdCie',
        numPolTiers: 'numPolTiers',
        numSinTiers: 'numSinTiers',
        risque1: 'risque1',
      };

      for (const [formKey, apiKey] of Object.entries(fieldMap)) {
        contextData[apiKey] = String(data[formKey] ?? '');
      }

      const response = await claimflowApiClient.post(
        '/claimflow/workflows/claimflow-add-new-stakeholder/execute',
        { context_data: contextData },
      );
      return response.data;
    },
    [idSin],
  );

  const baseConfig: EntityFormConfig = useMemo(
    () => ({
      id: 'intervenant-form',
      endpoint: {
        erpEndpoint: '/claimflow/erp/registries/veos/endpoints/sinper',
        method: 'POST',
      },
      fields: {
        existant: {
          type: 'checkbox',
          label: '',
          order: 1,
          colSpan: 2,
          options: {
            checkboxLabel: 'Existant',
          } as CheckboxFieldOptions,
        },
        categ: {
          type: 'select',
          label: 'Catégorie',
          order: 2,
          colSpan: 2,
          validation: { required: true },
          options: {
            items: categoryItems,
            searchable: true,
            placeholder: 'Sélectionner une catégorie',
          } as SelectFieldOptions,
        },
        // --- Existing person field ---
        idPer: {
          type: 'select',
          label: 'Identité',
          order: 3,
          visible: false,
          colSpan: 2,
          validation: { required: true },
          options: {
            items: personItems,
            searchable: true,
            placeholder: loadingPersons
              ? 'Chargement...'
              : 'Sélectionner une personne',
          } as SelectFieldOptions,
          conditions: [
            {
              when: 'existant',
              operator: 'equals',
              value: true,
              effect: { visible: true },
            },
          ],
        },
        // --- New person fields ---
        nom: {
          type: 'text',
          label: 'Nom',
          order: 4,
          visible: false,
          validation: { required: true },
          placeholder: 'Nom',
          conditions: [newStakeholderCondition],
        },
        prenom: {
          type: 'text',
          label: 'Prénom',
          order: 5,
          visible: false,
          placeholder: 'Prénom',
          conditions: [newStakeholderCondition],
        },
        mail: {
          type: 'email',
          label: 'Email',
          order: 6,
          visible: false,
          placeholder: 'email@exemple.com',
          conditions: [newStakeholderCondition],
        },
        tel: {
          type: 'tel',
          label: 'Téléphone',
          order: 7,
          visible: false,
          placeholder: '06 00 00 00 00',
          conditions: [newStakeholderCondition],
        },
        adresse1: {
          type: 'text',
          label: 'Adresse 1',
          order: 8,
          visible: false,
          colSpan: 2,
          placeholder: 'Adresse ligne 1',
          conditions: [newStakeholderCondition],
        },
        adresse2: {
          type: 'text',
          label: 'Adresse 2',
          order: 9,
          visible: false,
          colSpan: 2,
          placeholder: 'Adresse ligne 2',
          conditions: [newStakeholderCondition],
        },
        // Required on the new-person path: the `claimflow-add-new-stakeholder`
        // workflow feeds it to Veos' duplicate check (WS2_GS_VER_DOUBLON_PERSONNE
        // `cd_pst`). Hidden (so not validated) on the "Existant" path, exactly
        // like `nom`.
        cp: {
          type: 'text',
          label: 'CP',
          order: 10,
          visible: false,
          validation: { required: true },
          placeholder: '75000',
          conditions: [newStakeholderCondition],
        },
        ville: {
          type: 'text',
          label: 'Ville',
          order: 11,
          visible: false,
          placeholder: 'Paris',
          conditions: [newStakeholderCondition],
        },
        type: {
          type: 'select',
          label: 'Type',
          order: 12,
          visible: false,
          validation: { required: true },
          options: {
            items: [
              { value: 'P', label: 'Physique' },
              { value: 'M', label: 'Morale' },
            ],
            placeholder: 'Sélectionner un type',
          } as SelectFieldOptions,
          conditions: [newStakeholderCondition],
        },
        // `ref` is "non utilisé côté UI" per the mahhp 21464 spec — never
        // rendered. The value is still emitted (empty) by `mutationFn` below
        // so the workflow contract stays intact.
        ref: {
          type: 'text',
          label: 'Ref.',
          order: 13,
          visible: false,
        },
        cdCie: {
          type: 'select',
          label: 'Compagnie',
          order: 14,
          visible: false,
          options: {
            items: companyItems,
            searchable: true,
            placeholder: 'Sélectionner une compagnie',
          } as SelectFieldOptions,
          conditions: [newStakeholderCondition, tiersCondition],
        },
        numPolTiers: {
          type: 'text',
          label: 'No de police',
          order: 15,
          visible: false,
          placeholder: 'N° de police',
          conditions: [newStakeholderCondition, tiersCondition],
        },
        numSinTiers: {
          type: 'text',
          label: 'Ref sinistre tiers',
          order: 16,
          visible: false,
          placeholder: 'Référence sinistre tiers',
          conditions: [newStakeholderCondition, tiersCondition],
        },
        risque1: {
          type: 'text',
          label: 'Immat',
          order: 17,
          visible: false,
          placeholder: 'AA-000-AA',
          conditions: [newStakeholderCondition, tiersCondition],
        },
      },
      actions: {
        submit: { visible: true, label: "Ajouter l'intervenant" },
        cancel: { visible: false },
        reset: { visible: false },
      },
      layout: { columns: 2, gap: 'sm' },
    }),
    [categoryItems, personItems, companyItems, loadingPersons],
  );

  usePublishLayoutConfig(
    'claimflow.intervenantForm',
    buildPublishedConfigFromEntityForm(baseConfig, 'Ajouter un intervenant'),
  );

  const formConfig: EntityFormConfig = useMemo(
    () => applyLayoutOverrides(baseConfig, _layoutOverrides),
    [baseConfig, _layoutOverrides],
  );

  const handleSuccess = useCallback(() => {
    const values = formRef.current?.values ?? {};
    const categCode = String(values.categ ?? '');
    const categLabel =
      categories.find((c) => c.CD_CATEG === categCode)?.LIB_CATEG ?? categCode;

    let name: string;
    if (values.existant) {
      const personId = String(values.idPer ?? '');
      name = persons.find((p) => String(p.ID_PER) === personId)?.LIB ?? personId;
    } else {
      name = [values.nom, values.prenom].filter(Boolean).join(' ');
    }

    setSubmittedLabel(`${categLabel}: ${name}`);

    // The claim's intervenants changed — cards deriving data from them (the
    // Email card's recipient suggestions) re-fetch (mahhp 22850). `onSuccess`
    // only runs once the add mutation has resolved.
    useClaimDataStore.getState().bumpIntervenants();
  }, [categories, persons]);

  /**
   * Seed values for the form. `existant` takes the `existantByCategory`
   * entry of the seeded `defaultCategory` when the tenant mapped it (the
   * more specific rule wins over `defaultExistant`, so the mapped state is
   * already on screen at first paint and the form is not dirty on mount),
   * then falls back to `defaultExistant`, then to the historic `true`
   * literal; `categ` is only present when the tenant pins a
   * `defaultCategory`, so at baseline the object is exactly the original
   * `{ existant: true }` (an always-present `categ: undefined` key would
   * change `buildInitialValues`' output and the dirty-tracking key count).
   */
  const initialValues = useMemo(() => {
    const mappedExistant = defaultCategory
      ? existantByCategory?.[defaultCategory]
      : undefined;
    return {
      existant: mappedExistant ?? defaultExistant ?? true,
      ...(defaultCategory ? { categ: defaultCategory } : {}),
    };
  }, [defaultExistant, defaultCategory, existantByCategory]);

  const form = useEntityForm({
    config: formConfig,
    initialValues,
    onSuccess: handleSuccess,
    mutationFn,
  });

  formRef.current = form;

  // Sync local state with form values for dependent fetching
  const categValue = form.getValue('categ');
  const existantValue = form.getValue('existant');

  useEffect(() => {
    setSelectedCateg(typeof categValue === 'string' ? categValue : '');
  }, [categValue]);

  useEffect(() => {
    setExistant(existantValue === true);
  }, [existantValue]);

  // Read the tenant map through a ref: the HOC rebuilds the props record on
  // every render, so depending on the object itself would re-run the effect
  // (and re-apply the mapped value over a manual toggle) on an identity
  // change alone.
  const existantByCategoryRef = useRef(existantByCategory);
  existantByCategoryRef.current = existantByCategory;

  // Previous `categ` code, seeded with the one `initialValues` already
  // carries so the mapped seed is not re-applied on mount. Comparing against
  // it makes the effect below fire on a real category TRANSITION only —
  // unrelated re-renders (persons loading, card expand, …) are no-ops, which
  // is what keeps the checkbox togglable after the rule has applied.
  const prevCategRef = useRef<string>(defaultCategory ?? '');

  const setFormValue = form.setValue;

  useEffect(() => {
    const categCode = typeof categValue === 'string' ? categValue : '';
    if (categCode === prevCategRef.current) return;
    prevCategRef.current = categCode;

    // A category the tenant did not map (or a cleared selection) leaves
    // `existant` exactly as it is — no revert to a default.
    const mapped = existantByCategoryRef.current?.[categCode];
    if (typeof mapped === 'boolean') {
      setFormValue('existant', mapped);
    }
  }, [categValue, setFormValue]);

  if (submittedLabel) {
    return (
      <Card key="submitted" id={actionAnchorId('claimflow.intervenantForm')}>
        <Card.Header collapsible defaultExpanded={false}>
          <Icon icon={CheckCircle2} size="md" color="success" />
          <Text as="span" weight="semibold" size="base">
            {resolveCardTitle('Ajouter un intervenant', _layoutOverrides)}
          </Text>
          <Text as="span" size="sm" color="muted" className="ml-2">
            ({submittedLabel})
          </Text>
        </Card.Header>
        <Card.Content>
          {/* Chain to a NEW intervenant. Remounts the component (via the outer
              key counter) so the form returns to its exact initial state:
              empty values { existant: true }, collapsed editing card, and the
              active "Ajouter l'intervenant" submit button. */}
          <div className="pb-4">
            <Button
              variant="secondary"
              leftIcon={<RotateCcw className="w-4 h-4" />}
              onClick={onResetForm}
              className="w-full"
            >
              Nouvel intervenant
            </Button>
          </div>
          <EntityForm config={formConfig} form={form}>
            <EntityForm.Fields />
            <div className="pt-4">
              <Button
                variant="primary"
                leftIcon={<Save className="w-4 h-4" />}
                disabled
                className="w-full"
              >
                Intervenant ajouté
              </Button>
            </div>
          </EntityForm>
        </Card.Content>
      </Card>
    );
  }

  return (
    <Card key="open" id={actionAnchorId('claimflow.intervenantForm')}>
      <Card.Header
        collapsible
        defaultExpanded={expanded}
        onExpandChange={setExpanded}
      >
        <Icon icon={UserPlus} size="md" color="primary" />
        <Text as="span" weight="semibold" size="base">
          {resolveCardTitle('Ajouter un intervenant', _layoutOverrides)}
        </Text>
      </Card.Header>
      <Card.Content>
        <EntityForm config={formConfig} form={form}>
          <EntityForm.Alert />
          <EntityForm.Fields />
          {formConfig.actions?.submit?.visible !== false && (
            <div className="pt-4">
              <Button
                type="submit"
                variant="primary"
                leftIcon={<Save className="w-4 h-4" />}
                loading={form.isSubmitting}
                disabled={form.isSubmitting}
                className="w-full"
              >
                {formConfig.actions?.submit?.label ??
                  "Ajouter l'intervenant"}
              </Button>
            </div>
          )}
        </EntityForm>
      </Card.Content>
    </Card>
  );
}

export function IntervenantForm({
  ses,
  idSin,
  allowedCategories,
  defaultCategory,
  defaultExistant,
  existantByCategory,
  _layoutOverrides,
}: Readonly<IntervenantFormProps>) {
  // Remount key. Bumping it after a successful submit reloads the inner
  // component to its initial state, letting the user chain a NEW intervenant.
  // A full remount (rather than `form.reset()`) is used because resetting via
  // the hook does not clear the submission status held in `useFormSubmission`,
  // so a remount is the only way to guarantee an exact fresh-mount state.
  const [resetKey, setResetKey] = useState(0);

  const handleResetForm = useCallback(() => {
    setResetKey((k) => k + 1);
  }, []);

  return (
    <IntervenantFormInner
      key={resetKey}
      ses={ses}
      idSin={idSin}
      allowedCategories={allowedCategories}
      defaultCategory={defaultCategory}
      defaultExistant={defaultExistant}
      existantByCategory={existantByCategory}
      onResetForm={handleResetForm}
      _layoutOverrides={_layoutOverrides}
    />
  );
}
